/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {mat4, vec3} from "gl-matrix";
import IDataStorage from "App/storage/data_storage_interface";
import {DataResource} from "App/storage/data_resource";
// @ts-ignore
import DownloadWorker from "App/workers/download.worker";
import IScene from "js_interface/src/scene_interface";

export enum DownloadWorkerState {
    READY,
    WORKING
}

export class DownloadManager {

    private sceneAPI: IScene;
    private worker: any = null;
    private state: DownloadWorkerState = DownloadWorkerState.READY;
    private dataStorage: IDataStorage;
    private data: {
        returnSlots: {
            normal: number,
            highPr: number
        },
        resourceMessages: any[]
    };

    private readonly resourceUpdateCB: () => void;
    private resourceUpdateInterval: number;

    private delayedRecomputeDownloadPriorityMsg: {};
    private readonly priorityUpdateCB: () => void;
    private priorityUpdateInterval: number;

    constructor(aDataStorage: IDataStorage, aSceneAPI: IScene)
    {
        this.sceneAPI    = aSceneAPI;
        this.dataStorage = aDataStorage;

        this.initWorker();

        // asynchronous actions in order to handle large amount of changes in batches
        this.data = {
            resourceMessages: [],
            returnSlots:      { normal: 0, highPr: 0 }
        };

        this.resourceUpdateCB = (): void => {
            // this must be before the remove message
            if (this.data.returnSlots.normal > 0 || this.data.returnSlots.highPr > 0) {
                this.worker.postMessage({type: 'returnSlots', slots: this.data.returnSlots});
                this.data.returnSlots = { normal: 0, highPr: 0 };
            }

            if (this.data.resourceMessages.length > 0) {
                this.worker.postMessage({type: 'resourceMessages', resourceMessages: this.data.resourceMessages});
                this.data.resourceMessages = [];

                this._recomputeDownloadPriority();
            }

            // stop the interval in case we have no request to resolutions recompute
            clearInterval(this.resourceUpdateInterval);
            this.resourceUpdateInterval = 0;
        };

        this.resourceUpdateInterval = 0;

        this.delayedRecomputeDownloadPriorityMsg = null;
        this.priorityUpdateCB = (): void => {
            if (this.delayedRecomputeDownloadPriorityMsg) {
                if (this.state === DownloadWorkerState.READY) {
                    this.state = DownloadWorkerState.WORKING;
                    this.worker.postMessage(this.delayedRecomputeDownloadPriorityMsg);
                    this.delayedRecomputeDownloadPriorityMsg = null;
                }
            }
        };

        this.priorityUpdateInterval = 0;
    }

    public addResource(aResource: DataResource): void
    {
        let message = {
            url:          aResource.getUrl(),
            priority:     aResource.getPriority(),
            required:     aResource.isRequired(),
            localB64Data: "", // request for base64 data decoding
            type: 'addResource'
        };

        // if resource is local, then the data can be written directly in the resource object
        if (aResource.getIsLocal()) {
            message.localB64Data = aResource.getLocalBase64Data();
        }

        this.data.resourceMessages.push(message);
        this.scheduleDataUpdate();
    }

    /***
     * This will try to remove the resources which are not yet downloaded
     * @param aResource A resource to be removed from the download priority compute worker thread
     */
    public removeResource(aUrl: string): void
    {
        // tell the worker that the resource was removed
        let message = {
            url:   aUrl,
            type: 'removeResource'
        };

        this.data.resourceMessages.push(message);

        this.scheduleDataUpdate();
    }

    public attachShapeBBoxes(aUrl: string, aShapeBBoxes: any[])
    {
        let message = {
            url:    aUrl,
            bboxes:  aShapeBBoxes,
            type:   'attachShapeBBoxes'
        };

        this.data.resourceMessages.push(message);

        this.scheduleDataUpdate();
    }

    public updateShapeBBox(aUrl: string, aShapeUID: number, aBBoxMin: vec3 | number[], aBBoxMax: vec3 | number[])
    {
        let message = {
            url:      aUrl,
            shapeUID: aShapeUID,
            bboxMin:  aBBoxMin,
            bboxMax:  aBBoxMax,
            type:     'updateShapeBBox'
        };

        this.data.resourceMessages.push(message);

        this.scheduleDataUpdate();
    }

    public detachShapeBBox(aUrl: string, aShapeUID: number): void
    {
        let message = {
            url:      aUrl,
            shapeUID: aShapeUID,
            type:     'detachShapeBBox'
        };

        this.data.resourceMessages.push(message);

        this.scheduleDataUpdate();
    }

    public viewpointChanged(): void
    {
        this._recomputeDownloadPriority();
    }

    public returnSlots(aWorkerMessage: any): void
    {
        this.worker.postMessage({
            type: 'returnSlots',
            slots: {
                highPr: aWorkerMessage.highPrSlotUsed ? 1 : 0,
                normal: aWorkerMessage.highPrSlotUsed ? 0 : 1
            }
        });
    }

    public returnSlotsDelayed(aWorkerMessage: any): void
    {
        if (aWorkerMessage.highPrSlotUsed) {
            this.data.returnSlots.highPr++;
        }
        else {
            this.data.returnSlots.normal++;
        }

        this.scheduleDataUpdate();
    }

    private setDownloadSlotsSize(aNormalSlots: number, aHighPrSlots: number)
    {
        this.worker.postMessage({
            type: 'setDownloadSlotsSize',
            normalSlots: aNormalSlots,
            highPrSlots: aHighPrSlots
        });
    }

    private _recomputeDownloadPriority(): void
    {
        let message = {
            type: 'recomputeDownloadPriority',
            eyes: [],
            mvps: [],
            vpSizes: []
        };

        // collect all eyes, mvp and size of all views and send it to the resolution compute worker
        let viewMode = this.sceneAPI.viewModeModule;
        if (!viewMode) {
            return;
        }

        let views = viewMode.getViews();
        for (let view of views) {
            message.eyes.push(vec3.clone(view.getCamera().eye));
            message.mvps.push(mat4.clone(view.getCamera().getViewProjectionMatrix()));
            message.vpSizes.push(view.size);
        }

        this.delayedRecomputeDownloadPriorityMsg = message;
        this.schedulePriorityUpdate();
    }

    private initWorker(): void
    {
        // @ts-ignore
        this.worker = new DownloadWorker();
        this.createDownloadPriorityThreadListener();
    }

    private scheduleDataUpdate(): void
    {
        if (!this.resourceUpdateInterval) {
            this.resourceUpdateInterval = window.setInterval(this.resourceUpdateCB, 10);
        }
    }

    private schedulePriorityUpdate(): void
    {
        if (!this.priorityUpdateInterval) {
            this.priorityUpdateInterval = window.setInterval(this.priorityUpdateCB, 500);
        }
    }

    private createDownloadPriorityThreadListener(): void
    {
        this.worker.addEventListener('message', (e: any): void => {
            let message = e.data;
            switch (message.type) {
                case 'downloadResource':
                    let resource = this.dataStorage.getResource(message.url);
                    if (resource) {
                        resource.download(message);
                    } else {
                        // notify the worker thread that the resource does not exists so return the occupied download slot by this download request
                        this.returnSlots(message);
                    }
                    break;
                case 'downloadPrioritiesRecomputed':
                    this.state = DownloadWorkerState.READY;
                    break;
                default:
            }
        }, false);
    }
}
